import React, { useRef, useState, useCallback, useEffect } from 'react';
import { X, Maximize2, GripVertical, MessageSquare } from 'lucide-react';
import { ChatContainer } from './ChatContainer';
import { useChatStore } from '../../hooks/useChatStore';

interface ChatPanelProps {
  className?: string;
}

export function ChatPanel({ className = '' }: ChatPanelProps) {
  const { 
    displayMode, 
    panelWidth, 
    setPanelWidth, 
    closeChat, 
    expandToFullscreen, 
    collapseToPanel 
  } = useChatStore();
  
  const panelRef = useRef<HTMLDivElement>(null);
  const resizerRef = useRef<HTMLDivElement>(null);
  const [isResizing, setIsResizing] = useState(false);

  // Gestion du redimensionnement par drag
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizing) return;
    
    const container = panelRef.current?.parentElement;
    if (!container) return;
    
    const containerRect = container.getBoundingClientRect();
    const newWidth = ((containerRect.right - e.clientX) / containerRect.width) * 100;
    setPanelWidth(newWidth);
  }, [isResizing, setPanelWidth]);

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  if (displayMode === 'hidden') return null;

  // Mode plein écran
  if (displayMode === 'fullscreen') {
    return (
      <div className="fixed inset-0 z-50 bg-background">
        <ChatContainer 
          mode="fullscreen"
          onClose={closeChat}
          onModeChange={collapseToPanel}
        />
      </div>
    );
  }

  // Mode floating (modal)
  if (displayMode === 'floating') {
    return (
      <div className="fixed z-50">
        <ChatContainer 
          mode="floating"
          onClose={closeChat}
          onModeChange={expandToFullscreen}
        />
      </div>
    );
  }

  // Mode panel (côte à côte)
  return (
    <div 
      ref={panelRef}
      className={`
        relative flex flex-col bg-card border-l border-border
        h-full overflow-hidden
        ${className}
      `}
      style={{ width: `${panelWidth}%` }}
    >
      {/* Resizer - Barre de redimensionnement */}
      <div
        ref={resizerRef}
        onMouseDown={handleMouseDown}
        className={`
          absolute left-0 top-0 bottom-0 w-1 cursor-col-resize z-10
          hover:bg-primary/50 transition-colors
          ${isResizing ? 'bg-primary' : 'bg-transparent hover:bg-primary/30'}
        `}
      >
        {/* Grip indicator */}
        <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 p-1 rounded bg-muted border border-border opacity-0 hover:opacity-100 transition-opacity">
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </div>
      </div>

      {/* Header du panel */}
      <div className="flex items-center justify-between px-3 py-2 border-b bg-muted/30">
        <div className="flex items-center space-x-2">
          <div className="w-6 h-6 bg-primary/10 rounded flex items-center justify-center">
            <MessageSquare className="h-3 w-3 text-primary" />
          </div>
          <span className="text-sm font-medium">ADHA Assistant</span>
        </div>
        <div className="flex items-center space-x-1">
          <button
            onClick={expandToFullscreen}
            className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            title="Plein écran"
          >
            <Maximize2 className="h-4 w-4" />
          </button>
          <button
            onClick={closeChat}
            className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            title="Fermer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Contenu du chat */}
      <div className="flex-1 overflow-hidden">
        <ChatContainer 
          mode="fullscreen"
          onClose={closeChat}
          onModeChange={expandToFullscreen}
          embedded={true}
        />
      </div>
    </div>
  );
}
